import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ApiError, api } from "../../lib/api";
import { cn } from "../../lib/utils";
import { Td, Th, errMessage } from "./ui";
import { fmtEur0, fmtPx } from "../../lib/eex-format";

// P&L reclassification (admin journal layer): pick a delivery period, tick
// EQUAL lots of carried longs and opposing real fills — across books, no trade
// moves — choose the target cell, and the LOCKED SPREAD on those pairs books
// there instead of the delivery months. The server validates everything
// (capacity, exclusivity vs pins/pairs, full take in every month) and the
// recognition gate confirms the cell impact before anything lands.

interface Candidate {
  tradeId: string;
  side: "B" | "S";
  px: number;
  date: string | null;
  book: string | null;
  trader: string | null;
  source: string;
  // true = cascaded IN (a parcel root); false = the period's own fill (the
  // anchor's REAL leg). Every anchor pairs one of each.
  carried: boolean;
  lots: number;
}
interface CandidatesResp {
  asOf: string;
  period: { code: string; expYear: number; expMonth: number };
  periodLabel: { product: string; period: string };
  expectedMonths: string[];
  longs: Candidate[];
  shorts: Candidate[];
}
interface ImpactCell {
  month: string;
  book: string | null;
  trader: string | null;
  state: string;
  delta: number;
}
interface PreviewResp {
  amountEur: number;
  months: { fromMonth: string; book: string | null; trader: string | null; lots: number; eur: number }[];
  impact: { tier: "none" | "warning" | "locked"; reasons: string[]; affectedCells: ImpactCell[] };
}

export type ReclassTarget =
  | { code: string; expYear: number; expMonth: number }
  | { product: string; period: string };

const eurClass = (v: number) => (v > 0 ? "text-posGreen" : v < 0 ? "text-negRed" : "text-muted");

export function ReclassDialog({ target, onClose, onCreated }: {
  target: ReclassTarget;
  onClose: () => void;
  onCreated: () => void;
}) {
  const qc = useQueryClient();
  const params = new URLSearchParams(
    "code" in target
      ? { code: target.code, expYear: String(target.expYear), expMonth: String(target.expMonth) }
      : { product: target.product, period: target.period }
  );
  const q = useQuery({
    queryKey: ["eex-reclass-candidates", params.toString()],
    queryFn: () => api<CandidatesResp>(`/eex/recognition/reclass/candidates?${params.toString()}`),
  });

  const [tickedLongs, setTickedLongs] = useState<Set<string>>(new Set());
  const [tickedShorts, setTickedShorts] = useState<Set<string>>(new Set());
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [targetMonth, setTargetMonth] = useState("");
  const [targetBook, setTargetBook] = useState<string>("");
  const [targetTrader, setTargetTrader] = useState<string>("");
  const [preview, setPreview] = useState<PreviewResp | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const data = q.data;
  const pick = (rows: Candidate[], set: Set<string>) => rows.filter((c) => set.has(c.tradeId));
  const selLongs = data ? pick(data.longs, tickedLongs) : [];
  const selShorts = data ? pick(data.shorts, tickedShorts) : [];
  const longLots = selLongs.reduce((s, c) => s + c.lots, 0);
  const shortLots = selShorts.reduce((s, c) => s + c.lots, 0);
  const vwap = (rows: Candidate[]) => {
    const lots = rows.reduce((s, c) => s + c.lots, 0);
    return lots > 0 ? rows.reduce((s, c) => s + c.lots * c.px, 0) / lots : null;
  };

  // FIFO×FIFO pairing of the ticked sides into anchor legs: the CARRIED side
  // of each pair is the parcel root, the period's own fill is the real leg.
  // A valid selection is homogeneous per side (all-carried vs all-real).
  const legs = useMemo(() => {
    const L = selLongs.map((c) => ({ id: c.tradeId, left: c.lots, carried: c.carried }));
    const S = selShorts.map((c) => ({ id: c.tradeId, left: c.lots, carried: c.carried }));
    const out: { parcelRootTradeId: string; realTradeId: string; lots: number }[] = [];
    let i = 0, j = 0;
    while (i < L.length && j < S.length) {
      const take = Math.min(L[i]!.left, S[j]!.left);
      if (take > 0 && L[i]!.carried !== S[j]!.carried) {
        const root = L[i]!.carried ? L[i]! : S[j]!;
        const real = L[i]!.carried ? S[j]! : L[i]!;
        out.push({ parcelRootTradeId: root.id, realTradeId: real.id, lots: take });
      }
      L[i]!.left -= take;
      S[j]!.left -= take;
      if (L[i]!.left === 0) i += 1;
      if (S[j]!.left === 0) j += 1;
    }
    return out;
  }, [selLongs, selShorts]);

  const balanced = longLots > 0 && longLots === shortLots;
  const oneSideCarried =
    (selLongs.every((c) => c.carried) && selShorts.every((c) => !c.carried)) ||
    (selLongs.every((c) => !c.carried) && selShorts.every((c) => c.carried));
  const mixValid = balanced && oneSideCarried && legs.reduce((s, l) => s + l.lots, 0) === longLots;

  const body = () =>
    data && {
      name: name.trim() || `Reclass ${data.periodLabel.product} ${data.periodLabel.period}`,
      reason: reason.trim(),
      period: data.period,
      legs,
      targetMonth,
      targetBook: targetBook.trim() === "" ? null : targetBook.trim(),
      targetTrader: targetTrader.trim() === "" ? null : targetTrader.trim(),
    };

  const canAsk = !!data && mixValid && !!targetMonth && reason.trim().length >= 10;

  const doPreview = useMutation({
    mutationFn: () =>
      api<PreviewResp>("/eex/recognition/reclass/preview", { method: "POST", body: JSON.stringify(body()!) }),
    onSuccess: (r) => {
      setPreview(r);
      setMsg(null);
    },
    onError: (e) => setMsg(errMessage(e)),
  });

  const doCreate = useMutation({
    mutationFn: (confirmImpact: boolean) =>
      api<{ ok: true }>("/eex/recognition/reclass", {
        method: "POST",
        body: JSON.stringify({ ...body()!, ...(confirmImpact ? { confirmImpact: true } : {}) }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["eex-recognition-months"] });
      void qc.invalidateQueries({ queryKey: ["eex-worklist"] });
      void qc.invalidateQueries({ queryKey: ["eex-reclass"] });
      void qc.invalidateQueries({ queryKey: ["eex-recognition-fills"] });
      onCreated();
    },
    onError: (e: unknown) => {
      const errBody =
        e instanceof ApiError
          ? (e.body as { code?: string; impact?: PreviewResp["impact"]; amountEur?: number } | undefined)
          : undefined;
      if (errBody?.code === "confirm_required") {
        setConfirming(true);
        if (errBody.impact)
          setPreview((p) => ({
            amountEur: errBody.amountEur ?? p?.amountEur ?? 0,
            months: p?.months ?? [],
            impact: errBody.impact!,
          }));
        setMsg(null);
      } else {
        setMsg(errMessage(e));
      }
    },
  });

  const side = (title: string, rows: Candidate[], ticked: Set<string>, setTicked: (s: Set<string>) => void, cls: string) => (
    <div className="flex-1 min-w-[280px]">
      <div className={cn("font-medium border-b border-line pb-1 mb-1", cls)}>
        {title} · {rows.reduce((s, c) => s + c.lots, 0)} lots
      </div>
      <table className="w-full text-xs">
        <thead className="text-muted">
          <tr>
            <Th> </Th>
            <Th className="text-right">Lots</Th>
            <Th className="text-right">Px</Th>
            <Th>Date</Th>
            <Th>Book</Th>
            <Th>Source</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const on = ticked.has(c.tradeId);
            return (
              <tr
                key={c.tradeId}
                className="border-t border-line/40 hover:bg-line/20 cursor-pointer"
                onClick={() => {
                  const next = new Set(ticked);
                  if (on) next.delete(c.tradeId);
                  else next.add(c.tradeId);
                  setTicked(next);
                  setPreview(null);
                  setConfirming(false);
                }}
              >
                <Td>
                  <input type="checkbox" readOnly checked={on} />
                </Td>
                <Td className="text-right tabular-nums">{c.lots}</Td>
                <Td className="text-right tabular-nums">{fmtPx(c.px, 2)}</Td>
                <Td className="text-muted">{c.date ?? "—"}</Td>
                <Td className="text-muted">{c.book ?? "—"}</Td>
                <Td className={c.carried ? "text-sky-600 dark:text-sky-400" : "text-muted"}>
                  {c.carried ? "carried" : "traded"}
                </Td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <Td colSpan={6} className="text-muted italic py-2">
                nothing anchorable on this side
              </Td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-auto py-8" onClick={onClose}>
      <div
        className="bg-bg border border-line rounded-lg shadow-xl w-[min(96vw,980px)] p-4 space-y-3 text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <h2 className="font-semibold text-sm">
            Reclassify locked spread{data ? ` — ${data.periodLabel.product} ${data.periodLabel.period}` : ""}
          </h2>
          <button className="text-muted hover:text-fg" onClick={onClose}>
            close ✕
          </button>
        </div>
        <p className="text-muted max-w-3xl">
          Tick EQUAL lots on both sides — carried parcels against the period&apos;s own fills, across books, no
          trade moves. The pair&apos;s spread is settlement-independent, so it books as a FINAL amount in the
          target month; the delivery months are relieved of exactly the same lines. Reversible from the register.
        </p>

        {q.isLoading && <div className="text-muted">loading candidates…</div>}
        {q.isError && <div className="text-negRed">{errMessage(q.error)}</div>}

        {data && (
          <>
            <div className="text-muted">
              delivery months: {data.expectedMonths.join(", ")} · as of {data.asOf}
            </div>
            <div className="flex gap-4 flex-wrap">
              {side("Longs", data.longs, tickedLongs, setTickedLongs, "text-posGreen")}
              {side("Shorts", data.shorts, tickedShorts, setTickedShorts, "text-negRed")}
            </div>

            {(longLots > 0 || shortLots > 0) && (
              <div className="border border-line rounded px-3 py-1.5 flex items-center gap-3 flex-wrap bg-line/20">
                <span>
                  <b className="font-medium">{longLots}</b> long
                  {vwap(selLongs) != null && <> @ <span className="tabular-nums">{fmtPx(vwap(selLongs)!, 2)}</span></>}
                </span>
                <span className="text-muted">⇄</span>
                <span>
                  <b className="font-medium">{shortLots}</b> short
                  {vwap(selShorts) != null && <> @ <span className="tabular-nums">{fmtPx(vwap(selShorts)!, 2)}</span></>}
                </span>
                {!balanced && <span className="text-amber-600 dark:text-amber-400">sides must be EQUAL lots</span>}
                {balanced && vwap(selLongs) != null && vwap(selShorts) != null && (
                  <span className={cn("tabular-nums font-medium", eurClass(vwap(selShorts)! - vwap(selLongs)!))}>
                    locked {fmtPx(vwap(selShorts)! - vwap(selLongs)!, 2)}/MWh
                  </span>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <input
                className="bg-transparent border border-line rounded px-2 py-0.5 w-56"
                placeholder="entry name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <label className="text-muted">
                books into{" "}
                <input
                  type="month"
                  className="bg-transparent border border-line rounded px-2 py-0.5"
                  value={targetMonth}
                  onChange={(e) => {
                    setTargetMonth(e.target.value);
                    setPreview(null);
                    setConfirming(false);
                  }}
                />
              </label>
              <input
                className="bg-transparent border border-line rounded px-2 py-0.5 w-36"
                placeholder="target book"
                value={targetBook}
                onChange={(e) => {
                  setTargetBook(e.target.value);
                  setPreview(null);
                  setConfirming(false);
                }}
                list="reclass-books"
              />
              <datalist id="reclass-books">
                {[...new Set([...data.longs, ...data.shorts].map((c) => c.book).filter(Boolean))].map((b) => (
                  <option key={b} value={b!} />
                ))}
              </datalist>
              <input
                className="bg-transparent border border-line rounded px-2 py-0.5 w-40"
                placeholder="target trader"
                value={targetTrader}
                onChange={(e) => {
                  setTargetTrader(e.target.value);
                  setPreview(null);
                  setConfirming(false);
                }}
                list="reclass-traders"
              />
              <datalist id="reclass-traders">
                {[...new Set([...data.longs, ...data.shorts].map((c) => c.trader).filter(Boolean))].map((t) => (
                  <option key={t} value={t!} />
                ))}
              </datalist>
            </div>
            <textarea
              className="bg-transparent border border-line rounded px-2 py-1 w-full h-14"
              placeholder="reason (required, min 10 chars) — why this spread belongs in the target month; this is the audit record"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />

            {preview && (
              <div className="border border-line rounded px-3 py-2 space-y-1 bg-line/10">
                <div>
                  moves{" "}
                  <span className={cn("font-medium tabular-nums", eurClass(preview.amountEur))}>
                    {fmtEur0(preview.amountEur)}
                  </span>{" "}
                  into {targetMonth}
                  {preview.months.length > 0 && (
                    <span className="text-muted">
                      {" "}
                      out of{" "}
                      {preview.months
                        .map((m) => `${m.fromMonth} ${m.book ?? "(no book)"} (${m.lots} lots, ${fmtEur0(m.eur)})`)
                        .join(", ")}
                    </span>
                  )}
                </div>
                {preview.impact.affectedCells.length > 0 && (
                  <table className="text-[11px]">
                    <thead className="text-muted">
                      <tr>
                        <Th>Cell</Th>
                        <Th>State</Th>
                        <Th className="text-right">Δ</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.impact.affectedCells.map((c, i) => (
                        <tr key={i}>
                          <Td>
                            {c.month} · {c.book ?? "(no book)"} · {c.trader ?? "—"}
                          </Td>
                          <Td className="text-muted">{c.state}</Td>
                          <Td className={cn("text-right tabular-nums", eurClass(c.delta))}>{fmtEur0(c.delta)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {preview.impact.reasons.length > 0 && (
                  <div className="text-amber-600 dark:text-amber-400">{preview.impact.reasons.join("; ")}</div>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <button
                className="border border-line rounded px-2 py-0.5 text-muted hover:text-fg disabled:opacity-40"
                disabled={!canAsk || doPreview.isPending}
                onClick={() => doPreview.mutate()}
              >
                {doPreview.isPending ? "previewing…" : "Preview impact"}
              </button>
              {!confirming ? (
                <button
                  className="border border-posGreen/50 text-posGreen rounded px-2 py-0.5 font-medium hover:bg-posGreen/10 disabled:opacity-40 disabled:border-line disabled:text-muted"
                  disabled={!canAsk || doCreate.isPending}
                  onClick={() => doCreate.mutate(false)}
                >
                  Reclassify {balanced ? longLots : ""} lots → {targetMonth || "…"}
                </button>
              ) : (
                <button
                  className="border border-amber-500/60 text-amber-600 dark:text-amber-400 rounded px-2 py-0.5 font-medium hover:bg-amber-500/10 disabled:opacity-40"
                  disabled={doCreate.isPending}
                  onClick={() => doCreate.mutate(true)}
                >
                  Confirm — re-times booked months
                </button>
              )}
              {!canAsk && data && (
                <span className="text-[11px] text-muted">
                  {!balanced
                    ? "tick EQUAL lots on both sides"
                    : !oneSideCarried
                      ? "one whole side must be carried parcels, the other the period's own fills"
                      : !mixValid
                        ? "the ticked lots do not pair cleanly — adjust the selection"
                        : !targetMonth
                          ? "pick the target month"
                          : reason.trim().length < 10
                            ? "a reason of at least 10 characters is required"
                            : ""}
                </span>
              )}
              {msg && <span className="text-negRed">{msg}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
